import { createElement } from 'react';
import { HandshakeIcon } from 'lucide-react';

import { defineModule } from '@/shared/registry/types';

import { useCrmCommands, useCrmNavCounter, useLeadsAvailable } from './components/extensionHooks';
import { CrmSearchResult } from './components/extensions';
import { LazyCompanyPanel, LazyContactPanel, LazyDealPanel, LazyInvoicePanel, LazyItemPanel, LazyLeadPanel, LazyProfileTab, LazyQuotePanel } from './lazy';
import { ITEM_PANEL } from './model/entities';
import { setupCrmRealtime } from './realtime';

/**
 * `crm` module (docs/PRODUCT.md "CRM", docs/modules/crm.md): the dashboard, deals (pipelines, Kanban, list), leads,
 * contacts, companies, quotes, invoices, the product catalog, activities, import, duplicates and settings under
 * `/crm/*`; the printable documents at `/crm/print/:type/:id`; the `lead:`, `deal:`, `contact:`, `company:`, `quote:`,
 * `invoice:` and `crm-item:` (items of user-defined entity types) side panels (and `:new` forms); the profile tab `crm`; the sidebar counter of overdue activities; ⌘K
 * actions and search renderers.
 */
export const module = defineModule({
  id: 'crm',
  routes: [
    {
      path: 'crm/*',
      handle: { layout: 'full' },
      lazy: async () => ({ Component: (await import('./pages/CrmPage')).default }),
    },
  ],
  // The printable quote or invoice renders outside the shell (it signs in itself through RequireAuth).
  publicRoutes: [
    {
      path: 'crm/print/:type/:id',
      lazy: async () => ({ Component: (await import('./components/documents/PrintView')).default }),
    },
  ],
  nav: [
    {
      id: 'crm',
      labelKey: 'crm:nav.title',
      icon: HandshakeIcon,
      to: '/crm',
      order: 35,
      section: 'main',
      useCounter: useCrmNavCounter,
    },
  ],
  sidePanels: [
    { type: 'lead', component: LazyLeadPanel, size: 'xl', titleKey: 'crm:entities.lead', useAvailable: useLeadsAvailable },
    { type: 'deal', component: LazyDealPanel, size: 'xl', titleKey: 'crm:entities.deal' },
    { type: 'contact', component: LazyContactPanel, size: 'xl', titleKey: 'crm:entities.contact' },
    { type: 'company', component: LazyCompanyPanel, size: 'xl', titleKey: 'crm:entities.company' },
    { type: 'quote', component: LazyQuotePanel, size: 'xl', titleKey: 'crm:entities.quote' },
    { type: 'invoice', component: LazyInvoicePanel, size: 'xl', titleKey: 'crm:entities.invoice' },
    // Items of the user-defined entity types («Свои сущности»): `crm-item:<id>` and `crm-item:new`.
    { type: ITEM_PANEL, component: LazyItemPanel, size: 'xl', titleKey: 'crm:entities.item' },
  ],
  profileTabs: [
    {
      id: 'crm',
      labelKey: 'crm:tabs.profile',
      order: 60,
      element: (userId) => createElement(LazyProfileTab, { userId }),
    },
  ],
  searchRenderers: {
    lead: CrmSearchResult,
    deal: CrmSearchResult,
    contact: CrmSearchResult,
    company: CrmSearchResult,
    quote: CrmSearchResult,
    invoice: CrmSearchResult,
    item: CrmSearchResult,
  },
  commands: [{ id: 'crm-actions', headingKey: 'crm:commands.heading', order: 35, useItems: useCrmCommands }],
  setup: setupCrmRealtime,
});
